import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  Loader2,
  LayoutGrid,
  List,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock,
  Eye,
  Trash2,
  Calendar as CalendarIcon,
  Trophy,
  FileUp,
  UserPlus,
  HelpCircle,
  MessageSquareText,
  ListTodo,
  FileSpreadsheet,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { authClient } from "@/lib/auth-client";
import { useActiveProfile } from "@/providers/ActiveProfileProvider";
import { useTeam } from "@/hooks/queries/useTeam";
import {
  AssigneeSelect,
  MemberAvatar,
} from "@/components/team/AssigneeSelect";
import { useTeamMember } from "@/hooks/queries/useTeam";
import {
  LineRow,
  ResponsibleRow,
  ScopeRow,
  matchesSegment,
  type LineFilter,
  type Scope,
  type Segment,
} from "@/components/team/BoardFilterRows";
import { BoardHeader } from "@/components/team/BoardHeader";
import {
  TeamStats,
  MyStats,
  amountOf,
  nextDeadline,
} from "@/components/team/BoardStats";
import {
  AttentionPanel,
  FreeInMyLines,
  buildTeamAlerts,
} from "@/components/team/AttentionPanel";
import { ActivityFeed } from "@/components/team/ActivityFeed";
import { TeamManagement } from "@/components/team/TeamManagement";
import { LostReasonDialog } from "@/components/team/LostReasonDialog";
import {
  currentMonthKey,
  monthLabel,
  type MonthKey,
} from "@/components/team/MonthPicker";
import { useMonthlyStats } from "@/hooks/queries/useMonthlyStats";
import { useContractings } from "@/hooks/queries/useContractings";
import { useTeamActivity } from "@/hooks/queries/useTeamActivity";
import { useAssignContracting } from "@/hooks/mutations/useTeamMutations";
import { useOrganizationSearchProfiles } from "@/hooks/queries/useOrganizationSearchProfiles";
import { useUpdateContractingStatus } from "@/hooks/mutations/useContractingMutations";
import { useSuretyMode } from "@/hooks/useSuretyMode";
import { STATUS_LIST, ContractingUserStatus } from "@/lib/domain-constants";
import {
  formatMoney,
  formatMoneyCompact,
  parsePeruvianDate,
  displayedAmount,
} from "@/lib/format";
import { cn } from "@/lib/utils";

import { FilterBar, type FilterValues } from "@/components/filters/FilterBar";
import { ContractingTable } from "@/components/contractings/ContractingTable";
import { ContractingDetailSheet } from "@/components/contractings/ContractingDetailSheet";
import {
  StagePill,
  MinorBadge,
  FromBasesNote,
} from "@/components/contractings/pills";
import { MatchList } from "@/components/contractings/MatchPill";
import { QualificationBadge } from "@/components/contractings/QualificationBadge";
import { AiChatButton } from "@/components/common/AiChatButton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { API_URL } from "@/constants";
import type { ContractingItem, ScheduleStage } from "@/types/domain";

// A contracting is "saved" once it has any tracking status (including SIN_ESTADO),
// so the CRM board shows every status column — matching the legacy app.
const SAVED_STATUSES = STATUS_LIST.map((s) => s.value);

// El tablero pagina de verdad: 100 tarjetas por página. Antes pedía 200 de una y
// lo que sobraba se perdía sin aviso.
const PAGE_SIZE = 100;

/** Cómo se nombra una licitación en un aviso: la descripción, con la nomenclatura de respaldo. */
function cardLabel(item?: ContractingItem | null) {
  if (!item) return "la licitación";
  return item.contracting_description ?? item.contracting_nomenclature;
}

const MyContractingsCharts = lazy(() =>
  import("@/components/dashboard/MyContractingsCharts").then((m) => ({
    default: m.MyContractingsCharts,
  })),
);

type View = "board" | "list" | "calendar";

// Una columna vacía es una oportunidad de enseñar para qué sirve.
const EMPTY_COLUMN_HINT: Record<number, string> = {
  0: "Lo que se guardó y todavía nadie revisó.",
  1: "Revisadas y a la espera de una decisión.",
  2: "Arrastra aquí lo que ya está en conversación con la entidad.",
  3: "Sin ganadas registradas todavía.",
  4: "Registra el motivo al perder: sirve para el histórico.",
};
const EMPTY_FILTERS: FilterValues = {
  departmentIds: [],
  provinceIds: [],
  districtIds: [],
  dateFrom: "",
  dateTo: "",
  entityIds: [],
  subjects: [],
  stages: [],
  maxDays: null,
  onlyInterests: false,
  scope: "all",
};

function BoardCard({
  item,
  onView,
  onDragStart,
  onDragEnd,
  isDragging,
}: {
  item: ContractingItem;
  onView: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  isDragging: boolean;
}) {
  const mutation = useUpdateContractingStatus();
  const status = item.contractinguser?.contractinguser_status ?? 0;
  const tracking = item.contractinguser;
  const assignee = useTeamMember(tracking?.assignedUserId);
  const deadline = nextDeadline(item);
  const idleDays = tracking?.contractinguser_updatedat
    ? dayjs().diff(dayjs(tracking.contractinguser_updatedat), "day")
    : null;

  function move(delta: number, e: React.MouseEvent) {
    e.stopPropagation();
    const next = status + delta;
    if (next < 0 || next > 4) return;
    mutation.mutate(
      { contracting_id: item.contracting_id, status: next },
      { onError: () => toast.error("No se pudo mover la licitación") },
    );
  }

  function remove(e: React.MouseEvent) {
    e.stopPropagation();
    mutation.mutate(
      { contracting_id: item.contracting_id, status: null },
      { onError: () => toast.error("No se pudo quitar la licitación") },
    );
  }

  const cardAmount = displayedAmount(item);
  // En Fianzas lo que importa de una adjudicada es a quién afianzar.
  const isSurety = useSuretyMode();
  const winners = isSurety ? (item.winners ?? []) : [];

  return (
    <div
      role="button"
      tabIndex={0}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        // Firefox no arranca el arrastre sin payload, aunque no lo leamos.
        e.dataTransfer.setData("text/plain", String(item.contracting_id));
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onClick={onView}
      onKeyDown={(e) => {
        if (e.key === "Enter") onView();
      }}
      className={cn(
        "group relative flex w-full cursor-grab select-none flex-col gap-2 rounded-xl border border-slate-200/80 bg-card p-3 text-left transition-colors hover:border-slate-300 active:cursor-grabbing dark:border-slate-800 dark:hover:border-slate-700",
        isDragging && "opacity-40",
      )}
    >
      {/* Nomenclatura + etapa */}
      <div className="flex items-start gap-2">
        <span
          className="min-w-0 flex-1 truncate font-mono text-[10px] font-semibold uppercase tracking-tight text-slate-400"
          title={item.contracting_nomenclature}
        >
          {item.contracting_nomenclature}
        </span>
        {item.contracting_minorid && (
          <MinorBadge className="shrink-0 text-[9px]" />
        )}
        <StagePill
          stage={item.contractingversion_currentstagenormalized}
          className="shrink-0 text-[9px]"
        />
      </div>

      {/* Descripción y entidad: lo que identifica la licitación de un vistazo. */}
      <div className="flex flex-col gap-0.5">
        {item.contracting_description && (
          <p
            className="line-clamp-2 text-[13px] font-bold leading-snug text-slate-800 dark:text-slate-100"
            title={item.contracting_description}
          >
            {item.contracting_description}
          </p>
        )}
        {item.contracting_entity && (
          <p
            className="truncate text-[11px] text-slate-400 dark:text-slate-500"
            title={item.contracting_entity}
          >
            {item.contracting_entity}
          </p>
        )}
      </div>

      {/* Monto y el próximo plazo del cronograma, que es lo que apura. */}
      <div className="flex items-baseline justify-between gap-2">
        <div className="flex min-w-0 flex-col">
          <span className="font-mono text-sm font-bold tracking-tight text-slate-800 dark:text-slate-100">
            {cardAmount.amount === null
              ? `${item.currency?.currency_symbol ?? "S/"} -`
              : formatMoney(cardAmount.amount, item.currency?.currency_symbol)}
          </span>
          {cardAmount.fromBases && <FromBasesNote />}
        </div>
        <span
          className={cn(
            "shrink-0 text-[11px]",
            deadline && deadline.days <= 3
              ? "font-semibold text-amber-600 dark:text-amber-400"
              : "text-slate-400",
          )}
        >
          {deadline ? `${deadline.days} d` : "sin fecha"}
        </span>
      </div>

      {item.qualification && (
        <QualificationBadge qualification={item.qualification} />
      )}

      {winners.length > 0 && (
        <div className="flex flex-col gap-1 rounded-lg bg-slate-50 px-2 py-1.5 dark:bg-slate-800/40">
          {winners.map((winner) => (
            <div key={winner.name} className="flex min-w-0 flex-col">
              <span className="flex min-w-0 items-center gap-1 text-[11px] font-bold text-slate-700 dark:text-slate-200">
                <Trophy className="size-3 shrink-0 text-amber-500" />
                <span className="truncate" title={winner.name}>
                  {winner.name}
                </span>
              </span>
              {winner.members.length > 0 && (
                <span
                  className="line-clamp-2 text-[10px] text-slate-500 dark:text-slate-400"
                  title={winner.members.map((member) => member.name).join(", ")}
                >
                  {winner.members.map((member) => member.name).join(" · ")}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Lo que se le puede vender. Es el corazón de la tarjeta, no un adorno. */}
      {item.matchedInterests && item.matchedInterests.length > 0 && (
        <MatchList
          matches={item.matchedInterests}
          currencySymbol={item.currency?.currency_symbol}
          isMinor={!!item.contracting_minorid}
          compact
        />
      )}

      {/* Responsable + señales de trabajo */}
      <div className="mt-0.5 flex items-center justify-between gap-2 border-t border-slate-100 pt-2 dark:border-slate-800">
        {assignee ? (
          <span className="flex min-w-0 items-center gap-1.5">
            <MemberAvatar member={assignee} className="size-5" />
            <span className="truncate text-[11px] font-medium text-slate-600 dark:text-slate-300">
              {assignee.name}
            </span>
          </span>
        ) : (
          <span className="flex items-center gap-1.5 text-[11px] font-semibold text-red-500">
            <span className="flex size-5 items-center justify-center rounded-full border border-dashed border-red-300 dark:border-red-800">
              <UserPlus className="size-2.5" />
            </span>
            Sin responsable
          </span>
        )}
        {idleDays !== null && (
          <span
            className={cn(
              "shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-semibold",
              idleDays >= 7
                ? "bg-red-50 text-red-600 dark:bg-red-950/30 dark:text-red-400"
                : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
            )}
            title="Días desde el último movimiento"
          >
            {idleDays} d
          </span>
        )}
      </div>

      {/* Pie: lo que hay colgado de la licitación, en una sola línea. */}
      <div className="flex items-center gap-3 text-[10px] text-slate-400 dark:text-slate-500">
        <span>{item.documentCount ?? 0} docs</span>
        <span>{item.matchedInterests?.length ?? 0} ítems</span>
        <span>{tracking?.noteCount ?? 0} notas</span>
        {!!tracking?.openTaskCount && (
          <span>{tracking.openTaskCount} tareas</span>
        )}
      </div>

      {/* Acciones: aparecen al pasar el mouse para no competir con el contenido. */}
      <div className="absolute right-2 top-2 flex items-center gap-0.5 rounded-lg border bg-card p-0.5 opacity-0 shadow-sm transition-opacity focus-within:opacity-100 group-hover:opacity-100">
        <AssigneeSelect
          contractingId={item.contracting_id}
          assignedUserId={tracking?.assignedUserId}
          compact
        />
        <button
          type="button"
          disabled={status <= 0 || mutation.isPending}
          onClick={(e) => move(-1, e)}
          title="Mover a etapa anterior"
          className="flex size-6 cursor-pointer items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent dark:hover:bg-slate-800"
        >
          <ChevronLeft className="size-3.5" />
        </button>
        <button
          type="button"
          disabled={status >= 4 || mutation.isPending}
          onClick={(e) => move(1, e)}
          title="Mover a siguiente etapa"
          className="flex size-6 cursor-pointer items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent dark:hover:bg-slate-800"
        >
          <ChevronRight className="size-3.5" />
        </button>
        <button
          type="button"
          disabled={mutation.isPending}
          onClick={remove}
          title="Quitar de mis licitaciones"
          className="flex size-6 cursor-pointer items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500 disabled:opacity-30 dark:hover:bg-red-950/20"
        >
          <Trash2 className="size-3.5" />
        </button>
      </div>
    </div>
  );
}

interface CalendarEvent {
  contracting: ContractingItem;
  stage: ScheduleStage;
  date: dayjs.Dayjs;
}

// Tailwind classes per schedule stage, mirroring the Angular calendar.
function stageStyle(etapa: string): {
  bg: string;
  text: string;
  border: string;
  dot: string;
} {
  const name = etapa.toLowerCase();
  if (name.includes("buena pro"))
    return {
      bg: "bg-emerald-50 hover:bg-emerald-100",
      text: "text-emerald-700",
      border: "border-emerald-200",
      dot: "bg-emerald-500",
    };
  if (name.includes("presentación") || name.includes("presentacion"))
    return {
      bg: "bg-rose-50 hover:bg-rose-100",
      text: "text-rose-700",
      border: "border-rose-200",
      dot: "bg-rose-500",
    };
  if (name.includes("registro") || name.includes("participantes"))
    return {
      bg: "bg-blue-50 hover:bg-blue-100",
      text: "text-blue-700",
      border: "border-blue-200",
      dot: "bg-blue-500",
    };
  if (name.includes("consultas") || name.includes("observaciones"))
    return {
      bg: "bg-amber-50 hover:bg-amber-100",
      text: "text-amber-700",
      border: "border-amber-200",
      dot: "bg-amber-500",
    };
  return {
    bg: "bg-slate-50 hover:bg-slate-100",
    text: "text-slate-700",
    border: "border-slate-200",
    dot: "bg-slate-400",
  };
}

function StageIcon({
  etapa,
  className,
}: {
  etapa: string;
  className?: string;
}) {
  const name = etapa.toLowerCase();
  if (name.includes("buena pro")) return <Trophy className={className} />;
  if (name.includes("presentación") || name.includes("presentacion"))
    return <FileUp className={className} />;
  if (name.includes("registro")) return <UserPlus className={className} />;
  if (name.includes("consultas")) return <HelpCircle className={className} />;
  return <CalendarIcon className={className} />;
}

function EventCard({
  event,
  onSelect,
}: {
  event: CalendarEvent;
  onSelect: (id: number) => void;
}) {
  const { contracting: c, stage, date } = event;
  const s = stageStyle(stage.etapa);
  const time = stage.fechaInicio.includes(" ")
    ? stage.fechaInicio.split(" ")[1]
    : null;
  return (
    <div className="flex flex-col rounded-2xl border bg-slate-50/40 p-3 transition-shadow hover:shadow-xs">
      <div className="flex items-start justify-between gap-2">
        <span
          className={cn(
            "shrink-0 rounded-full border px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wider",
            s.bg,
            s.text,
            s.border,
          )}
        >
          {stage.etapa}
        </span>
        <StageIcon
          etapa={stage.etapa}
          className={cn("size-4 shrink-0", s.text)}
        />
      </div>
      <button
        onClick={() => onSelect(c.contracting_id)}
        className="mt-3 text-left text-xs font-extrabold tracking-tight hover:underline"
      >
        {c.contracting_nomenclature}
      </button>
      {c.contracting_minorid && (
        <MinorBadge className="mt-1.5 self-start text-[9px]" />
      )}
      {c.contracting_description && (
        <span className="mt-1 line-clamp-2 text-[11px] leading-relaxed text-muted-foreground">
          {c.contracting_description}
        </span>
      )}
      {c.matchedInterests && c.matchedInterests.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {c.matchedInterests.map((m, i) => (
            <span
              key={`${m.description}-${i}`}
              title={m.description}
              className="inline-flex max-w-full items-center gap-1 truncate rounded border border-primary/20 bg-primary/5 px-1.5 py-0.5 text-[9px] font-semibold text-primary"
            >
              <span className="size-1 shrink-0 rounded-full bg-primary" />
              <span className="truncate font-bold">{m.description}</span>
              <span className="shrink-0 font-mono font-extrabold">
                {formatMoney(m.amount)}
              </span>
            </span>
          ))}
        </div>
      )}
      <div className="mt-3 flex items-center justify-between border-t pt-3 text-[10px] font-bold text-muted-foreground">
        <div className="flex items-center gap-1">
          <CalendarIcon className="size-3.5" />
          <span>Inicio: {date.format("DD/MM/YYYY")}</span>
        </div>
        {time && (
          <div className="flex items-center gap-0.5">
            <Clock className="size-3.5" />
            <span>{time}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function CalendarView({
  items,
  onSelect,
}: {
  items: ContractingItem[];
  onSelect: (id: number) => void;
}) {
  const [month, setMonth] = useState(dayjs().startOf("month"));
  // Day key ("YYYY-MM-DD") whose milestones are pinned in the sidebar, if any.
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const today = dayjs();

  // Flatten every contracting's schedule stages into dated calendar events.
  const events = useMemo(() => {
    const all: CalendarEvent[] = [];
    for (const c of items) {
      if (!c.contractingversion_schedule) continue;
      for (const stage of c.contractingversion_schedule) {
        const date = parsePeruvianDate(stage.fechaInicio);
        if (!date) continue;
        all.push({ contracting: c, stage, date });
      }
    }
    return all;
  }, [items]);

  const byDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const e of events) {
      const key = e.date.format("YYYY-MM-DD");
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(e);
    }
    return map;
  }, [events]);

  // Upcoming milestones: future stage starts, sorted chronologically, max 15.
  const upcoming = useMemo(() => {
    const startOfToday = dayjs().startOf("day");
    return events
      .filter((e) => !e.date.isBefore(startOfToday))
      .sort((a, b) => a.date.valueOf() - b.date.valueOf())
      .slice(0, 15);
  }, [events]);

  const selectedDayEvents = selectedDay ? (byDay.get(selectedDay) ?? []) : [];

  // Force Sunday-first grid regardless of locale.
  const firstOfMonth = month.startOf("month");
  const gridStart = firstOfMonth.subtract(firstOfMonth.day(), "day");
  const days = Array.from({ length: 42 }, (_, i) => gridStart.add(i, "day"));
  const monthLabel =
    month.format("MMMM [de] YYYY").charAt(0).toUpperCase() +
    month.format("MMMM [de] YYYY").slice(1);

  return (
    <div className="flex flex-col gap-4 lg:flex-row">
      {/* Calendar */}
      <div className="flex-1 rounded-2xl bg-card p-4 shadow-sm">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="size-8 rounded-full"
              onClick={() => setMonth((m) => m.subtract(1, "month"))}
            >
              <ChevronLeft className="size-4" />
            </Button>
            <h3 className="min-w-[150px] text-center font-bold">
              {monthLabel}
            </h3>
            <Button
              variant="outline"
              size="icon"
              className="size-8 rounded-full"
              onClick={() => setMonth((m) => m.add(1, "month"))}
            >
              <ChevronRight className="size-4" />
            </Button>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMonth(dayjs().startOf("month"))}
          >
            Hoy
          </Button>
        </div>

        <div className="grid grid-cols-7 border-b text-center text-xs font-medium text-muted-foreground">
          {["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"].map((d) => (
            <div key={d} className="py-2">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {days.map((day) => {
            const key = day.format("YYYY-MM-DD");
            const dayEvents = byDay.get(key) ?? [];
            const inMonth = day.month() === month.month();
            const isToday = day.isSame(today, "day");
            const isSelected = selectedDay === key;
            return (
              <div
                key={key}
                role="button"
                tabIndex={0}
                onClick={() =>
                  setSelectedDay((cur) => (cur === key ? null : key))
                }
                onKeyDown={(ev) => {
                  if (ev.key === "Enter")
                    setSelectedDay((cur) => (cur === key ? null : key));
                }}
                className={cn(
                  "min-h-[92px] cursor-pointer border-b border-r p-1.5 transition-colors hover:bg-muted/40",
                  !inMonth && "bg-muted text-muted-foreground",
                  isSelected && "bg-primary/5 ring-2 ring-inset ring-primary",
                )}
              >
                <span
                  className={cn(
                    "inline-flex size-6 items-center justify-center rounded-full text-xs font-medium",
                    isToday && "bg-primary font-bold text-primary-foreground",
                  )}
                >
                  {day.date()}
                </span>
                <div className="mt-1 flex max-h-[80px] flex-col gap-1 overflow-y-auto">
                  {dayEvents.slice(0, 3).map((e) => {
                    const s = stageStyle(e.stage.etapa);
                    return (
                      <button
                        key={`${e.contracting.contracting_id}-${e.stage.etapa}`}
                        onClick={(ev) => {
                          ev.stopPropagation();
                          onSelect(e.contracting.contracting_id);
                        }}
                        title={`${e.contracting.contracting_nomenclature} - ${e.stage.etapa}`}
                        className={cn(
                          "flex items-center gap-1 truncate rounded-md border px-1.5 py-0.5 text-left text-[9px] font-medium transition-shadow hover:shadow-xs",
                          s.bg,
                          s.text,
                          s.border,
                        )}
                      >
                        <span
                          className={cn(
                            "size-1.5 shrink-0 rounded-full",
                            s.dot,
                          )}
                        />
                        <span className="shrink-0 truncate font-extrabold">
                          {e.contracting.contracting_nomenclature
                            .split("-")
                            .slice(0, 6)
                            .join("-")}
                        </span>
                        <span className="truncate">{e.stage.etapa}</span>
                      </button>
                    );
                  })}
                  {dayEvents.length > 3 && (
                    <span className="pl-1 text-[9px] font-bold text-muted-foreground">
                      +{dayEvents.length - 3} más
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Sidebar: día seleccionado o próximos hitos */}
      <aside className="rounded-2xl bg-card p-5 shadow-sm lg:w-96">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
              {selectedDay ? "Hitos del día" : "Cronograma"}
            </p>
            <h3 className="font-bold">
              {selectedDay
                ? dayjs(selectedDay).format("DD MMM, YYYY")
                : "Próximos Hitos"}
            </h3>
          </div>
          {selectedDay ? (
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              title="Ver próximos hitos"
              onClick={() => setSelectedDay(null)}
            >
              <X className="size-4" />
            </Button>
          ) : (
            <Clock className="size-4 text-muted-foreground" />
          )}
        </div>
        <div className="my-4 border-t" />
        {selectedDay ? (
          selectedDayEvents.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
              <CalendarIcon className="size-8 text-muted-foreground/40" />
              <p className="text-sm italic text-muted-foreground">
                Sin hitos para este día
              </p>
            </div>
          ) : (
            <div className="flex max-h-[500px] flex-col gap-3 overflow-y-auto pr-1">
              {selectedDayEvents.map((e) => (
                <EventCard
                  key={`${e.contracting.contracting_id}-${e.stage.etapa}`}
                  event={e}
                  onSelect={onSelect}
                />
              ))}
            </div>
          )
        ) : upcoming.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
            <CalendarIcon className="size-8 text-muted-foreground/40" />
            <p className="text-sm italic text-muted-foreground">
              Sin hitos en los próximos 30 días
            </p>
          </div>
        ) : (
          <div className="flex max-h-[500px] flex-col gap-3 overflow-y-auto pr-1">
            {upcoming.map((e) => (
              <EventCard
                key={`${e.contracting.contracting_id}-${e.stage.etapa}-${e.date.valueOf()}`}
                event={e}
                onSelect={onSelect}
              />
            ))}
          </div>
        )}
      </aside>
    </div>
  );
}

export default function MyContractings() {
  const { profiles, isLoading: profilesLoading } = useActiveProfile();
  const [view, setView] = useState<View>("board");
  const [selected, setSelected] = useState<number | null>(null);
  const [filters, setFilters] = useState<FilterValues>(EMPTY_FILTERS);
  const [line, setLine] = useState<LineFilter>(null);
  const [page, setPage] = useState(0);
  const [dragging, setDragging] = useState<number | null>(null);
  const [dragOverStatus, setDragOverStatus] = useState<number | null>(null);
  const [losing, setLosing] = useState<{
    contractingId: number;
    label: string;
  } | null>(null);
  const [scope, setScope] = useState<Scope>("mine");
  const [manageOpen, setManageOpen] = useState(false);
  // El mes del que se habla: manda sobre la banda de métricas, no sobre el
  // tablero, que siempre muestra el pipeline de hoy.
  const [month, setMonth] = useState<MonthKey>(currentMonthKey);

  const { data: session } = authClient.useSession();
  const { data: team } = useTeam();
  const isTeam = !!team?.isTeam;
  const leadsTeam = isTeam && !!team?.canManageSearchProfiles;
  const myId = session?.user.id;
  // En Fianzas no hay líneas de negocio: el pipeline sale de la cartera y del Mercado.
  const isSurety = useSuretyMode();

  // Quien dirige entra viendo a todo su equipo; el comercial, a lo suyo.
  const [segment, setSegment] = useState<Segment | undefined>(undefined);
  const activeSegment = segment === undefined ? null : segment;

  const assign = useAssignContracting();
  const updateStatus = useUpdateContractingStatus();
  const { data: activity = [] } = useTeamActivity(
    leadsTeam ? "team" : "mine",
    isTeam,
  );
  // Solo para contar cuántas líneas lleva cada comercial en los chips.
  const { data: orgProfiles } = useOrganizationSearchProfiles(leadsTeam);
  const { data: monthlyStats = [], isLoading: monthsLoading } = useMonthlyStats(
    leadsTeam ? "team" : "mine",
  );

  // El pipeline es de la EMPRESA y no cuelga de ninguna línea, así que "todas las
  // líneas" es una sola consulta sin perfil. Elegir una línea sí acota, con
  // `onlyInterests`: sin eso el selector no cambiaba ni una fila.
  const queryFilters = useMemo(
    () => ({
      searchProfileId: line ?? undefined,
      onlyInterests: line !== null,
      assignedTo: null,
      departmentId: filters.departmentIds,
      dateFrom: filters.dateFrom || null,
      dateTo: filters.dateTo || null,
      procuringEntityId: filters.entityIds,
      stage: filters.stages,
      subject: filters.subjects,
      // "all" no viaja: es el default del backend y evita ensuciar la URL.
      scope: filters.scope && filters.scope !== "all" ? filters.scope : null,
      status: SAVED_STATUSES,
      page,
      size: PAGE_SIZE,
    }),
    [filters, line, page],
  );

  const { data: listData, isLoading } = useContractings(queryFilters);
  const allItems = useMemo(
    () => listData?.contractings ?? [],
    [listData],
  );
  const pagination = listData?.pagination;

  useEffect(() => {
    if (segment !== undefined || !team) return;
    setSegment(leadsTeam ? null : (myId ?? null));
  }, [segment, team, leadsTeam, myId]);

  // Cambiar de línea o de filtro empieza de nuevo: la página 3 de otra búsqueda
  // no significa nada.
  useEffect(() => setPage(0), [line, filters]);

  const mineItems = useMemo(
    () =>
      allItems.filter((item) => item.contractinguser?.assignedUserId === myId),
    [allItems, myId],
  );

  // Lo que se pinta: el que dirige filtra por responsable; el comercial, por
  // alcance (su cartera o todo lo que la empresa trabaja en sus líneas).
  const items = useMemo(() => {
    if (!isTeam) return allItems;
    if (leadsTeam)
      return allItems.filter((item) => matchesSegment(item, activeSegment));
    return scope === "mine" ? mineItems : allItems;
  }, [allItems, mineItems, isTeam, leadsTeam, activeSegment, scope]);

  const freeInMyLines = useMemo(
    () => allItems.filter((item) => !item.contractinguser?.assignedUserId),
    [allItems],
  );

  const lineCountByUser = useMemo(() => {
    const map = new Map<string, number>();
    for (const profile of orgProfiles ?? []) {
      for (const user of profile.assignedUsers) {
        map.set(user.id, (map.get(user.id) ?? 0) + 1);
      }
    }
    return map;
  }, [orgProfiles]);

  const activeMembers = useMemo(
    () => team?.members.filter((member) => member.isActive) ?? [],
    [team],
  );

  const alerts = useMemo(
    () =>
      buildTeamAlerts(leadsTeam ? allItems : mineItems, activeMembers, setSelected),
    [leadsTeam, allItems, mineItems, activeMembers],
  );

  const segmentLabel = useMemo(() => {
    if (activeSegment === null) return "todo el equipo";
    if (activeSegment === "unassigned") return "sin responsable";
    return (
      activeMembers.find((member) => member.userId === activeSegment)?.name ??
      "equipo"
    );
  }, [activeSegment, activeMembers]);

  const byStatus = useMemo(() => {
    const map = new Map<number, ContractingItem[]>();
    for (const meta of STATUS_LIST) map.set(meta.value, []);
    for (const c of items) {
      const s = c.contractinguser?.contractinguser_status ?? 0;
      if (map.has(s)) map.get(s)!.push(c);
    }
    return map;
  }, [items]);

  // Arrastrar una tarjeta a otra columna es cambiar su estado. Perder pide el
  // motivo antes de escribir: sin él, el histórico de perdidas no dice nada.
  function dropOn(status: number) {
    const contractingId = dragging;
    setDragging(null);
    if (contractingId == null) return;

    const current = allItems.find((c) => c.contracting_id === contractingId);
    if ((current?.contractinguser?.contractinguser_status ?? 0) === status) return;

    if (status === ContractingUserStatus.PERDIDA) {
      setLosing({ contractingId, label: cardLabel(current) });
      return;
    }

    updateStatus.mutate(
      { contracting_id: contractingId, status },
      { onError: () => toast.error("No se pudo mover la licitación") },
    );
  }

  function confirmLoss(reason: string) {
    if (!losing) return;
    updateStatus.mutate(
      {
        contracting_id: losing.contractingId,
        status: ContractingUserStatus.PERDIDA,
        lost_reason: reason,
      },
      {
        onSuccess: () => setLosing(null),
        onError: () => toast.error("No se pudo registrar la pérdida"),
      },
    );
  }

  // Descarga por fetch y no por <a href>: la sesión va por cookie y hace falta
  // `credentials: include`, que un enlace suelto no manda al backend en otro host.
  async function exportPipeline() {
    try {
      const response = await fetch(
        `${API_URL}/api/contractings/export.csv${
          isTeam && !leadsTeam && scope === "mine" ? "?scope=mine" : ""
        }`,
        { credentials: "include" },
      );
      if (!response.ok) throw new Error("export failed");

      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = url;
      link.download = `pipeline-${dayjs().format("YYYY-MM-DD")}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo exportar el pipeline");
    }
  }

  function takeContracting(contractingId: number) {
    if (!myId) return;
    assign.mutate(
      { contracting_id: contractingId, assigned_user_id: myId },
      {
        onSuccess: () => toast.success("La tomaste: ya figura a tu nombre."),
        onError: () => toast.error("No se pudo tomar la licitación."),
      },
    );
  }

  function onFilterChange(patch: Partial<FilterValues>) {
    setFilters((f) => ({ ...f, ...patch }));
  }

  if (profilesLoading) {
    return (
      <div className="flex flex-1 items-center justify-center py-24">
        <Loader2 className="size-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full flex-1 px-4 py-6 md:px-14">
      <BoardHeader
        isTeam={isTeam}
        leadsTeam={leadsTeam}
        memberCount={team?.members.length ?? 1}
        organizationName={team?.organization?.name ?? null}
        month={month}
        months={monthlyStats.map((stat) => stat.month)}
        onMonthChange={setMonth}
        lineLabel={
          isSurety
            ? null
            : line === null
              ? "Todas las líneas"
              : (profiles.find((p) => p.searchprofile_id === line)
                  ?.searchprofile_name ?? "Línea")
        }
        me={
          myId
            ? {
                id: myId,
                name: session?.user.name ?? "",
                image: session?.user.image,
              }
            : null
        }
        onManage={isSurety ? undefined : () => setManageOpen(true)}
      />

      <div className="mb-6 rounded-2xl border bg-card shadow-sm">
        <LineRow
          profiles={profiles}
          value={line}
          onChange={setLine}
          leadsTeam={leadsTeam}
          hideLines={isSurety}
        >
          <FilterBar
            value={filters}
            onChange={onFilterChange}
            showContratado={false}
            showOnlyInterests={false}
            showScope
          />
        </LineRow>

        {leadsTeam && (
          <ResponsibleRow
            items={allItems}
            members={activeMembers}
            segment={activeSegment}
            onSegmentChange={setSegment}
            visibleCount={items.length}
            lineCountByUser={isSurety ? undefined : lineCountByUser}
          />
        )}

        {isTeam && !leadsTeam && (
          <ScopeRow
            scope={scope}
            onScopeChange={setScope}
            visibleCount={items.length}
          />
        )}
      </div>

      {leadsTeam ? (
        <TeamStats
          items={allItems}
          members={activeMembers}
          segmentLabel={segmentLabel}
          monthStat={monthlyStats.find((stat) => stat.month === month)}
          monthLabel={monthLabel(month)}
          monthlyStats={monthlyStats}
          month={month}
          monthsLoading={monthsLoading}
        />
      ) : isTeam ? (
        <MyStats mine={mineItems} inMyLines={allItems} />
      ) : null}


      {!isTeam && (
        <div className="mb-6">
          <Suspense
            fallback={<Skeleton className="h-[260px] w-full rounded-2xl" />}
          >
            <MyContractingsCharts contractings={items} />
          </Suspense>
        </div>
      )}

      {/* Listado */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-bold tracking-tight">
          {leadsTeam ? "Pipeline de seguimiento" : "Mi pipeline"}
          <span className="ml-2 text-sm font-normal text-slate-400">
            {view === "board"
              ? "arrastra una tarjeta para cambiar su estado comercial"
              : `${pagination?.length ?? items.length} en seguimiento`}
          </span>
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="cursor-pointer"
            onClick={exportPipeline}
            title="Descargar el pipeline en CSV"
          >
            <FileSpreadsheet className="size-4 text-emerald-600 dark:text-emerald-400" />{" "}
            Exportar
          </Button>
        <Tabs value={view} onValueChange={(v) => setView(v as View)}>
          <TabsList className="h-auto gap-1 rounded-xl bg-white p-1">
            <TabsTrigger
              value="board"
              className="gap-1.5 rounded-lg px-4 py-2 data-[state=active]:text-primary data-[state=active]:bg-primary/10 cursor-pointer"
            >
              <LayoutGrid className="size-4" /> Tablero
            </TabsTrigger>
            <TabsTrigger
              value="list"
              className="gap-1.5 rounded-lg px-4 py-2 data-[state=active]:text-primary data-[state=active]:bg-primary/10 cursor-pointer"
            >
              <List className="size-4" /> Lista
            </TabsTrigger>
            <TabsTrigger
              value="calendar"
              className="gap-1.5 rounded-lg px-4 py-2 data-[state=active]:text-primary data-[state=active]:bg-primary/10 cursor-pointer"
            >
              <CalendarDays className="size-4" /> Calendario
            </TabsTrigger>
          </TabsList>
        </Tabs>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="size-6 animate-spin text-primary" />
        </div>
      ) : view === "board" ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
          {STATUS_LIST.map((meta) => {
            const cards = byStatus.get(meta.value) ?? [];
            const isDropTarget = dragOverStatus === meta.value;
            return (
              <div
                key={meta.value}
                onDragOver={(e) => {
                  if (dragging == null) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  setDragOverStatus(meta.value);
                }}
                onDragLeave={() =>
                  setDragOverStatus((cur) => (cur === meta.value ? null : cur))
                }
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOverStatus(null);
                  dropOn(meta.value);
                }}
                // El tinte sale del color del propio estado, así que agregar una
                // columna no obliga a inventar una clase nueva. Va translúcido
                // para que funcione igual sobre fondo claro y oscuro.
                style={{
                  backgroundColor: `color-mix(in srgb, ${meta.color} ${
                    isDropTarget ? 14 : 7
                  }%, transparent)`,
                }}
                className={cn(
                  "flex min-h-[520px] flex-col rounded-2xl transition-colors",
                  isDropTarget && "ring-2 ring-primary ring-offset-2",
                )}
              >
                <div className="flex items-center gap-2 px-3 py-2.5">
                  <span
                    className="size-2 rounded-full"
                    style={{ backgroundColor: meta.color }}
                  />
                  <h3 className="text-[13px] font-bold">{meta.label}</h3>
                  <span className="text-xs text-slate-400">{cards.length}</span>
                  <span className="ml-auto font-mono text-[11px] text-slate-400">
                    {formatMoneyCompact(
                      cards.reduce((sum, c) => sum + amountOf(c), 0),
                    )}
                  </span>
                </div>
                <div className="flex-1 px-2 pb-2">
                  {cards.length === 0 ? (
                    <div
                      style={{
                        borderColor: `color-mix(in srgb, ${meta.color} 28%, transparent)`,
                      }}
                      className="flex items-center justify-center rounded-xl border border-dashed px-4 py-10 text-center"
                    >
                      <span className="text-xs leading-relaxed text-slate-400">
                        {EMPTY_COLUMN_HINT[meta.value] ?? "Sin licitaciones"}
                      </span>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {cards.map((c) => (
                        <BoardCard
                          key={c.contracting_id}
                          item={c}
                          onView={() => setSelected(c.contracting_id)}
                          onDragStart={() => setDragging(c.contracting_id)}
                          onDragEnd={() => {
                            setDragging(null);
                            setDragOverStatus(null);
                          }}
                          isDragging={dragging === c.contracting_id}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : view === "list" ? (
        <div className="lg:overflow-hidden lg:rounded-2xl lg:bg-card lg:shadow-sm">
          <ContractingTable
            items={items}
            onRowClick={(c) => setSelected(c.contracting_id)}
            emptyLabel="Aún no tienes licitaciones en seguimiento."
            showTracking
          />
        </div>
      ) : (
        <CalendarView items={items} onSelect={setSelected} />
      )}

      {pagination && pagination.lastPage > 1 && (
        <div className="mt-6 flex items-center justify-center gap-3">
          <Button
            variant="outline"
            size="sm"
            className="cursor-pointer"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            <ChevronLeft className="size-4" /> Anterior
          </Button>
          <span className="text-xs text-slate-400">
            Página {page + 1} de {pagination.lastPage} · {pagination.length} en
            seguimiento
          </span>
          <Button
            variant="outline"
            size="sm"
            className="cursor-pointer"
            disabled={page + 1 >= pagination.lastPage}
            onClick={() => setPage((p) => p + 1)}
          >
            Siguiente <ChevronRight className="size-4" />
          </Button>
        </div>
      )}

      {isTeam && (
        <div className="mt-8 grid gap-4 lg:grid-cols-2">
          {leadsTeam ? (
            <AttentionPanel title="Requiere atención" alerts={alerts} />
          ) : (
            <>
              <AttentionPanel
                title="Para hoy"
                alerts={alerts}
                footnote="no depende del filtro"
              />
              <FreeInMyLines
                items={freeInMyLines}
                onTake={takeContracting}
                onOpen={setSelected}
                isTaking={assign.isPending}
              />
            </>
          )}
          <ActivityFeed
            title={leadsTeam ? "Actividad del equipo" : "Mi actividad"}
            entries={activity}
            currentUserId={myId}
            onOpen={setSelected}
            footnote={
              leadsTeam ? (
                <>
                  {!isSurety && (
                    <>
                      {line === null ? "Líneas de negocio: " : "Línea de negocio: "}
                      <span className="font-semibold text-slate-600 dark:text-slate-300">
                        {line === null
                          ? `${profiles.length}`
                          : (profiles.find((p) => p.searchprofile_id === line)
                              ?.searchprofile_name ?? "—")}
                      </span>
                      {" · "}
                    </>
                  )}
                  {`${team?.members.length ?? 0} persona${
                    (team?.members.length ?? 0) === 1 ? "" : "s"
                  }`}
                </>
              ) : undefined
            }
          />
        </div>
      )}

      <LostReasonDialog
        open={losing !== null}
        label={losing?.label ?? ""}
        isSaving={updateStatus.isPending}
        onConfirm={confirmLoss}
        onCancel={() => setLosing(null)}
      />

      <TeamManagement open={manageOpen} onOpenChange={setManageOpen} />

      <AiChatButton />

      <ContractingDetailSheet
        contractingId={selected}
        searchProfileId={line ?? undefined}
        locked={
          items.find((c) => c.contracting_id === selected)?.isLocked === true
        }
        open={selected != null}
        onOpenChange={(o) => !o && setSelected(null)}
      />
    </div>
  );
}
